import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
	parseISO,
	addDays,
	format,
} from 'date-fns';
import AirbnbIcon from '../Assets/airbnb-icon.svg';
import ExpediaIcon from '../Assets/Expedia-icon.svg';
import BookingIcon from '../Assets/Booking-icon.svg';
import KozyguruIcon from '../Assets/Kozyguru-Icon.svg';
import AgodaIcon from '../Assets/agoda-icon.png';
import DirectIcon from '../Assets/Kozyguru-Icon.svg';
import SyncIcon from '../Assets/arrow-round-left.svg';

import PopUp from '../components/PopUp';
import 'react-tooltip/dist/react-tooltip.css';
import {
	getCalendarBatchData,
	getListingById,
	getListingsByUserId,
	getBatchReportingData,
	updateCalendarPricesBulk
} from '../services/api';
import usePermissions from '../hooks/usePermissions';
import { PERMISSIONS } from '../constants/permissions';
import {
	startOfWeek,
	endOfWeek,
	isSameDay,
	addMonths,
} from 'date-fns';
import runningModel from '../Assets/models/running.glb';
import { Dropdown } from '../components/Dropdown';

import {
	Container,
	LeftContainer,
	Title,
	Subtitle,
	RightContainer,
	DaySpan,
	GlobalStyles,
	ListingWrap,
	InfoCardGroup,
	InfoCard,
	ListingHeader,
	LoadingWrapper,
	ErrorP,
	ErrorWarp,
	ErrorImg,
	ToastContainer,
	ToastCloseButton,
	UpdateIndicator,
	SpinnerIcon,
	PriceTag,
	PopupOverlay,
	PopupContent
} from '../style-comps/CalenderSingle.style';


const customButtonStyles = `
  /* 所有自訂按鈕的基本樣式 */
  .fc-button-group {
    display: flex !important;
    gap: 8px !important;
  }

  .fc-prevCustom-button,
  .fc-nextCustom-button,
  .fc-today-button,
  .fc-refreshCustom-button {
    background-color: #61cbc9 !important;
    border: none !important;
    padding: 8px 12px !important;
    border-radius: 6px !important;
    color: white !important;
    font-weight: 500 !important;
    cursor: pointer !important;
    transition: all 0.3s !important;
    margin: 0 !important;
  }

  .fc-prevCustom-button:hover,
  .fc-nextCustom-button:hover,
  .fc-today-button:hover,
  .fc-refreshCustom-button:hover {
    background-color: #4fb3b1 !important;
    opacity: 0.9 !important;
  }

  .fc-button-disabled {
    opacity: 0.5 !important;
    cursor: not-allowed !important;
  }

  /* 🔄 Refresh 按鈕特殊樣式 */
  .fc-refreshCustom-button {
    min-width: 35px !important;
    display: inline-flex !important;
    align-items: center !important;
    justify-content: center !important;
  }

  /* 🔄 Refresh 按鈕圖示：改用 CSS 直接顯示，不再靠 JS 注入 <img>（避免首次載入 race condition） */
  .fc-refreshCustom-button {
    font-size: 0 !important;
  }

  .fc-refreshCustom-button::before {
    content: '' !important;
    display: block !important;
    width: 24px !important;
    height: 24px !important;
    background: url(${SyncIcon}) center / contain no-repeat !important;
    filter: brightness(0) invert(1) !important;
    transition: transform 0.3s ease !important;
  }

  /* 刷新中動畫 */
  .fc-refreshCustom-button.refreshing::before {
    transform: rotate(180deg) !important;
  }

  /* ✅ 讓 toolbar 左右分開 */
  .fc-toolbar-chunk:first-child {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  .fc-toolbar-chunk:last-child {
    margin-left: auto;
  }
`;

// 將樣式注入頁面
if (typeof document !== 'undefined') {
	const styleSheet = document.createElement("style");
	styleSheet.innerText = customButtonStyles;
	document.head.appendChild(styleSheet);
}


// ✅ 圖示與樣式一併設定
const channelStyles = {
	airbnb: {
		icon: AirbnbIcon,
		borderColor: '#F75C60',
		backgroundColor: '#F75C60',
	},
	expedia: {
		icon: ExpediaIcon,
		borderColor: '#FDDB32',
		backgroundColor: '#FDDB32',
	},
	'booking.com': {
		icon: BookingIcon,
		borderColor: '#003B95',
		backgroundColor: '#003B95',
	},
	agoda: {
		icon: AgodaIcon,
		borderColor: '#E52833',
		backgroundColor: '#E52833',
	},
	kozyguru: {
		icon: DirectIcon,
		borderColor: '#61CBC9',
		backgroundColor: '#61CBC9',
	},
	vrbo: {
		icon: ExpediaIcon,
		borderColor: '#FDDB32',
		backgroundColor: '#FDDB32',
	},
	default: {
		icon: KozyguruIcon,
		borderColor: '#61CBC9',
		backgroundColor: '#61CBC9',
	},

};






const isMobileDevice = () =>
	typeof window !== 'undefined' &&
	window.innerWidth <= 768;
const reservationCache = new Map();
const listingDataCache = new Map();

const CalendarSingle = () => {
	const permissions = usePermissions();
	const { listingId } = useParams();

	// 🆕 功能開關
	const ENABLE_EDITING = true;     // 控制編輯功能（改為 true）
	const ENABLE_PRICE_VIEW = true;  // 控制價格顯示

	const permissionChecks = {
		canViewCalendar: permissions.hasPermission(PERMISSIONS.CALENDAR_READ),
		canViewPrices: ENABLE_PRICE_VIEW && permissions.hasPermission(PERMISSIONS.LISTINGS_VIEW_PRICES),
		canEditPrices: ENABLE_EDITING && permissions.hasPermission(PERMISSIONS.LISTINGS_EDIT_PRICES),
	};


	const [events, setEvents] = useState([]);
	const [property, setProperty] = useState(null);
	const [popupData, setPopupData] = useState(null);
	const [isUpdatingPrice, setIsUpdatingPrice] = useState(false);
	const [showToast, setShowToast] = useState(false);
	const [toastMessage, setToastMessage] = useState('');
	const [isRefreshing, setIsRefreshing] = useState(false);
	const [updatingDates, setUpdatingDates] = useState(new Set());
	const [reservationMap, setReservationMap] = useState({});
	const [isLoadingMonth, setIsLoadingMonth] = useState(false);
	const [dateRange, setDateRange] = useState({
		start: null,
		end: null,
	});
	const [minDate, setMinDate] = useState(null);
	const [maxDate, setMaxDate] = useState(null);
	const [currentDate, setCurrentDate] = useState(
		new Date()
	);
	const calendarRef = useRef(null);
	const showLoadingRef = useRef(true);
	const reportingDataRef = useRef([]);
	const rawCalendarDataRef = useRef([]);
	const [rawCalendarData, setRawCalendarData] = useState(
		[]
	);

	const [reportingData, setReportingData] = useState([]);
	const [currentRevenue, setCurrentRevenue] =
		useState(null);
	const [currentOccupancy, setCurrentOccupancy] =
		useState(null);
	const [currentReservations, setCurrentReservations] =
		useState(null);
	const [
		averageRevenuePerNight,
		setAverageRevenuePerNight,
	] = useState(null);
	const [errorMessage, setErrorMessage] = useState('');
	const [reservationDateRange, setReservationDateRange] =
		useState({ start: null, end: null });
	const [reportingMonth, setReportingMonth] =
		useState(null);
	const [totalNights, setTotalNights] = useState(null);
	const [showLoading, setShowLoading] = useState(true);

	useEffect(() => {
		showLoadingRef.current = showLoading;
	}, [showLoading]);

	useEffect(() => {
		reportingDataRef.current = reportingData;
	}, [reportingData]);

	useEffect(() => {
		rawCalendarDataRef.current = rawCalendarData;
	}, [rawCalendarData]);

	const [fadeOut, setFadeOut] = useState(false);
	const [progress, setProgress] = useState(0);
	const [displayProgress, setDisplayProgress] =
		useState(0);
	useEffect(() => {
		if (displayProgress === progress) return;
		const step = progress > displayProgress ? 1 : -1;
		const interval = setInterval(() => {
			setDisplayProgress((prev) => {
				if (step > 0 && prev >= progress) {
					clearInterval(interval);
					return progress;
				}
				if (step < 0 && prev <= progress) {
					clearInterval(interval);
					return progress;
				}
				return prev + step;
			});
		}, 20); // 數字變動速度（越小越快）
		return () => clearInterval(interval);
	}, [progress]);


	const [reportingLoading, setReportingLoading] =
		useState(true);




	const handleDatesSet = async (viewMid) => {
		const newMonth = new Date(viewMid.getFullYear(), viewMid.getMonth(), 1);
		setReportingMonth(newMonth);

		if (showLoadingRef.current) return;

		const monthStr = format(newMonth, 'yyyy-MM');
		const hasReportingData = reportingDataRef.current.some(r =>
			r.StartDate?.slice(0, 7) === monthStr
		);
		const hasCalendarData = rawCalendarDataRef.current.some(d => {
			const rawDate = d.Date || d.date || '';
			return rawDate.slice(0, 7) === monthStr;
		});

		if (hasCalendarData && hasReportingData) return;

		try {
			setIsLoadingMonth(true);
			const mStart = format(new Date(newMonth.getFullYear(), newMonth.getMonth(), 1), 'yyyy-MM-dd');
			const mEnd = format(new Date(newMonth.getFullYear(), newMonth.getMonth() + 1, 0), 'yyyy-MM-dd');

			const [v2Response, calendarResponse] = await Promise.all([
				!hasReportingData ? getBatchReportingData([Number(listingId)], mStart, mEnd) : Promise.resolve(null),
				!hasCalendarData ? getCalendarBatchData({ listingIds: [listingId], startDate: mStart, endDate: mEnd, perPage: 300, page: 1 }) : Promise.resolve(null)
			]);

			if (v2Response) {
				const v2Months = v2Response?.Success?.[0]?.Months || [];
				const match = v2Months.find(m => m.MonthKey === monthStr);
				if (match) {
					setReportingData(prev => [
						...prev.filter(r => r.StartDate?.slice(0, 7) !== monthStr),
						{ StartDate: mStart, EndDate: mEnd, ...match.Reporting }
					]);
				}
			}

			if (calendarResponse) {
				const batchItem = calendarResponse?.success?.[0] || calendarResponse?.Success?.[0] || {};
				const newDays = batchItem.calendar || batchItem.Calendar || [];
				const newRawData = [...rawCalendarDataRef.current, ...newDays];
				setRawCalendarData(newRawData);

				// 從新資料建立 reservation events
				const reservationById = {};
				newRawData.forEach(day => {
					if (day.ReservationId && day.CheckIn && day.CheckOut) {
						reservationById[day.ReservationId] = {
							Id: day.ReservationId,
							ListingId: day.ListingId || Number(listingId),
							CheckIn: day.CheckIn,
							CheckOut: day.CheckOut,
							Nights: day.Nights,
							// Guests: day.Guests,
							Source: day.Source,
							Currency: day.Currency,
							OwnerRevenue: day.Revenue,
							Status: 'accepted'
						};
					}
				});

				const allReservationDetails = Object.values(reservationById);
				const newEvents = allReservationDetails.map((res) => {
					const cleanedSource = cleanSource(res.Source);
					const checkInDate = format(parseISO(res.CheckIn), 'yyyy-MM-dd');
					const nights = res.Nights || 1;
					const endDate = format(addDays(parseISO(res.CheckIn), nights), 'yyyy-MM-dd');
					const style = channelStyles[cleanedSource.toLowerCase()] || channelStyles.default;
					return {
						id: `res-${res.Id}`,
						title: cleanedSource,
						start: checkInDate,
						end: endDate,
						backgroundColor: style.backgroundColor,
						extendedProps: {
							...res,
							Source: cleanedSource,
							Currency: res.Currency || 'AUD',
						},
					};
				});

				const newReservationMap = {};
				newReservationMap[listingId] = allReservationDetails.filter(res =>
					res.Status === 'accepted' || res.Status === 'Accepted'
				);
				setEvents(newEvents);
				setReservationMap(newReservationMap);
			}
		} catch (err) {
			console.error('❌ 補抓月份資料失敗', err);
		} finally {
			setIsLoadingMonth(false);
		}
	};


	const [hasNoListings, setHasNoListings] =
		useState(false);
	const [currentCurrency, setCurrentCurrency] =
		useState('AUD');
	const [userListings, setUserListings] = useState([]);
	const [selectedListingId, setSelectedListingId] =
		useState(listingId);
	const [dropdownOpen, setDropdownOpen] = useState(false);

	// URL 參數變更（含瀏覽器上一頁/下一頁）時同步下拉選單
	useEffect(() => {
		setSelectedListingId(listingId);
	}, [listingId]);
	const [key, setKey] = useState(0);
	const navigate = useNavigate();


	// 跳脫 HTML，避免 API 回傳的字串被當成 HTML 注入 tooltip（XSS）
	const escapeHtml = (value) =>
		String(value ?? '').replace(/[&<>"']/g, (ch) => ({
			'&': '&amp;',
			'<': '&lt;',
			'>': '&gt;',
			'"': '&quot;',
			"'": '&#39;',
		}[ch]));

	const showTooltip = (content, e) => {
		const tooltip = document.getElementById(
			'global-tooltip'
		);
		if (!tooltip) return;
		tooltip.innerHTML = content;
		tooltip.style.display = 'block';
		tooltip.style.top = `${e.clientY - 28}px`;
		tooltip.style.left = `${e.clientX}px`;
	};

	const hideTooltip = () => {
		const tooltip = document.getElementById(
			'global-tooltip'
		);
		if (tooltip) tooltip.style.display = 'none';
	};

	//篩選平台來源
	const cleanSource = (rawSource) => {
		if (!rawSource) return 'KozyGuru';
		const lower = rawSource.toLowerCase();
		if (lower.includes('expedia')) return 'Expedia';
		if (lower.includes('marriott')) return 'Marriott';
		if (lower.includes('airbnb')) return 'Airbnb';
		if (lower.includes('booking')) return 'Booking.com';
		if (lower.includes('vrbo')) return 'VRBO';
		if (lower.includes('agoda')) return 'Agoda';
		if (
			lower.includes('direct') ||
			lower.includes('kozyguru')
		)
			return 'KozyGuru';
		return rawSource.trim(); // 👈 其他沒命中的來源會直接顯示原字串
	};

	// 只建立一次 Tooltip DOM
	useEffect(() => {
		if (!document.getElementById('global-tooltip')) {
			const tooltip = document.createElement('div');
			tooltip.id = 'global-tooltip';
			Object.assign(tooltip.style, {
				position: 'fixed',
				background: '#61CBC9',
				color: 'white',
				fontSize: '12px',
				padding: '8px 10px',
				borderRadius: '6px',
				zIndex: 9999,
				pointerEvents: 'none',
				whiteSpace: 'nowrap',
				display: 'none',
				transition: 'opacity 0.15s ease',
			});
			document.body.appendChild(tooltip);
		}

		// 離開頁面時隱藏 tooltip，避免殘留在畫面上
		return () => {
			const tooltip = document.getElementById('global-tooltip');
			if (tooltip) tooltip.style.display = 'none';
		};
	}, []);

	// 🆕 自訂 refresh 按鈕的圖示
	useEffect(() => {
		const timer = setTimeout(() => {
			const refreshButton = document.querySelector('.fc-refreshCustom-button');
			if (refreshButton) {
				if (isRefreshing) {
					refreshButton.classList.add('refreshing');
					refreshButton.disabled = true;
				} else {
					refreshButton.classList.remove('refreshing');
					refreshButton.disabled = false;
				}
			}
		}, 50);

		return () => clearTimeout(timer);
	}, [isRefreshing]);

	useEffect(() => {
		const clearOnTouch = () => hideTooltip();
		document.addEventListener(
			'touchstart',
			clearOnTouch
		);
		return () =>
			document.removeEventListener(
				'touchstart',
				clearOnTouch
			);
	}, []);
	const minAllowedRef = useRef(null);
	const maxAllowedRef = useRef(null);

	useEffect(() => {
		const now = new Date();
		const minAllowed = new Date(
			now.getFullYear(),
			now.getMonth() - 3,
			1
		);
		const maxAllowed = new Date(
			now.getFullYear(),
			now.getMonth() + 4,
			0
		); // 該月最後一天

		minAllowedRef.current = minAllowed;
		maxAllowedRef.current = maxAllowed;

		setMinDate(minAllowed);
		setMaxDate(maxAllowed);
	}, []);

	const formatCurrencySymbol = (currency) => {
		switch (currency) {
			case 'AUD': return '$';
			case 'GBP': return '£';
			case 'USD': return '$';
			default: return '$';
		}
	};

	useEffect(() => {
		const fetchUserListings = async () => {
			const token = localStorage.getItem('authToken');
			const userId = localStorage.getItem('userId');
			if (!token || !userId) return;

			try {
				const listings = await getListingsByUserId(
					userId,
					token
				);
				setUserListings(listings);
			} catch (err) {
				console.error('Failed to fetch user listings:', err);
			}
		};

		fetchUserListings();
	}, []);

	useEffect(() => {
		const fetchAllData = async () => {
			if (!listingId) return;
			const token = localStorage.getItem('authToken');
			if (!token) {
				console.error('No token, cannot load data');
				return;
			}
			if (listingDataCache.has(listingId)) {
				const cached =
					listingDataCache.get(listingId);
				console.log(
					`⚡ 使用快取資料 for listing ${listingId}`
				);
				setProperty(cached.property);
				setRawCalendarData(cached.calendarData);
				setReportingData(cached.reportingData);
				setTimeout(() => {
					setEvents(cached.events);
				}, 0);
				setCurrentRevenue(cached.currentRevenue);
				setCurrentOccupancy(
					cached.currentOccupancy
				);
				setCurrentReservations(
					cached.currentReservations
				);
				setAverageRevenuePerNight(
					cached.averageRevenuePerNight
				);
				setTotalNights(cached.totalNights);
				setCurrentCurrency(cached.currentCurrency);
				setReservationMap(cached.reservationMap || {});

				// ✅ 強制讓畫面顯示今日月份
				setCurrentDate(new Date());
				setReportingMonth(new Date());

				setShowLoading(false);
				return;
			}

			const now = new Date();

			try {
				setShowLoading(true);
				setFadeOut(false);
				setProgress(5);


				// V2 API：一次打回所有月份
				const reportStart = format(
					new Date(now.getFullYear(), now.getMonth() - 1, 1),
					'yyyy-MM-dd'
				);
				const reportEnd = format(
					new Date(now.getFullYear(), now.getMonth() + 2, 0),
					'yyyy-MM-dd'
				);
				// monthRanges 保留給後面 calendar 使用
				const monthRanges = [];
				for (let i = -1; i <= 1; i++) {
					const targetMonth = new Date(now.getFullYear(), now.getMonth() + i, 1);
					const mStart = format(targetMonth, 'yyyy-MM-dd');
					const mEnd = format(
						new Date(targetMonth.getFullYear(), targetMonth.getMonth() + 1, 0),
						'yyyy-MM-dd'
					);
					monthRanges.push({ start: mStart, end: mEnd });
				}

				const reportingPromise = getBatchReportingData([Number(listingId)], reportStart, reportEnd);

				// 第一段：只抓 ±1 個月，快速顯示
				const calendarStart = format(
					new Date(now.getFullYear(), now.getMonth() - 1, 1),
					'yyyy-MM-dd'
				);
				const calendarEnd = format(
					new Date(now.getFullYear(), now.getMonth() + 2, 0),
					'yyyy-MM-dd'
				);

				const calendarPromise = getCalendarBatchData({
					listingIds: [Number(listingId)],
					startDate: calendarStart,
					endDate: calendarEnd,
					perPage: 217,
					page: 1
				});

				// 🔹 Listing Promise
				const listingPromise = getListingById(
					listingId,
					token
				);
				setProgress(15);

				const progressInterval = setInterval(() => {
					setProgress(prev => {
						if (prev >= 38) {
							clearInterval(progressInterval);
							return 38;
						}
						return prev + 1;
					});
				}, 400);

				// 三路並行：報表 + Listing + Calendar
				const [
					reportingResultsRaw,
					listing,
					calendarResults,
				] = await Promise.all([
					reportingPromise,
					listingPromise,
					calendarPromise,
				]);
				clearInterval(progressInterval);
				setProgress(40);

				// ✅ 從 batch 回傳裡取出 calendar 資料
				const rawDays = calendarResults?.success?.[0]?.calendar || [];

				// 轉換成跟原本 calendarData 一樣的格式
				const calendarData = rawDays.map(day => ({
					Date: day.Date ? day.Date.split('T')[0] : null,
					Status: day.Status,
					Price: day.Price,
					Currency: day.Currency,
					Note: day.Note,
					ListingId: day.ListingId || Number(listingId),
					ReservationId: day.ReservationId,
					CheckIn: day.CheckIn,
					CheckOut: day.CheckOut,
					Nights: day.Nights,
					Source: day.Source,
					Revenue: day.Revenue,
				}));

				// ✅ V2 結構轉換：把 Months[] 轉成跟舊的 reportingArray 一樣的格式
				setProgress(50);
				const v2Months = reportingResultsRaw?.Success?.[0]?.Months || [];
				console.log("v2Months from API:", v2Months);
				console.log("monthKeys we expect:", monthRanges.map(r => r.start.slice(0, 7)));

				const reportingArray = monthRanges.map(({ start, end }) => {
					const monthKey = start.slice(0, 7); // "2026-02"
					const found = v2Months.find(m => m.MonthKey === monthKey);
					const r = found?.Reporting;
					return r
						? {
							StartDate: start,
							EndDate: end,
							OccupancyRate: r.OccupancyRate || 0,
							TotalOwnerRevenue: r.TotalOwnerRevenue || 0,
							TotalReservations: r.TotalReservations || 0,
							TotalNights: r.TotalNights || 0,
							AverageNights: r.AverageNights || 0,
							AverageRevenuePerNight: r.AverageRevenuePerNight || 0,
							Currency: r.Currency || 'AUD',
						}
						: {
							StartDate: start,
							EndDate: end,
							OccupancyRate: 0,
							TotalOwnerRevenue: 0,
							TotalReservations: 0,
							TotalNights: 0,
							AverageNights: 0,
							AverageRevenuePerNight: 0,
							Currency: 'AUD',
						};
				});

				// 🔹 更新 state
				setProperty(listing);
				setRawCalendarData(calendarData);
				setReportingData(reportingArray);
				setEvents([]);
				setProgress(55);

				// ✅ 設定當月報表
				const currentMonthStart = new Date(
					now.getFullYear(),
					now.getMonth(),
					1
				);
				setReportingMonth(currentMonthStart);
				const currentMonthEnd = new Date(
					now.getFullYear(),
					now.getMonth() + 1,
					0
				);
				const currentReport = reportingArray.find(
					(r) => {
						const start = new Date(r.StartDate);
						const end = new Date(r.EndDate);
						return (
							start.getFullYear() ===
							currentMonthStart.getFullYear() &&
							start.getMonth() ===
							currentMonthStart.getMonth() &&
							end.getFullYear() ===
							currentMonthEnd.getFullYear() &&
							end.getMonth() ===
							currentMonthEnd.getMonth()
						);
					}
				);

				if (currentReport) {
					setCurrentRevenue(
						currentReport.TotalOwnerRevenue || 0
					);
					setCurrentOccupancy(
						currentReport.OccupancyRate || 0
					);
					setCurrentReservations(
						currentReport.TotalReservations || 0
					);
					setAverageRevenuePerNight(
						currentReport.AverageRevenuePerNight ||
						0
					);
					setTotalNights(
						currentReport.TotalNights || 0
					);
					setCurrentCurrency(
						currentReport.Currency || 'AUD'
					);
				}

				// ✅ 直接從 calendarData 建 reservation，不需要再打 getReservationById
				setProgress(60);

				const reservationById = {};
				calendarData.forEach(day => {
					if (
						day.ReservationId &&
						day.CheckIn &&
						day.CheckOut
					) {
						reservationById[day.ReservationId] = {
							Id: day.ReservationId,
							ListingId: day.ListingId || Number(listingId),
							CheckIn: day.CheckIn,
							CheckOut: day.CheckOut,
							// Guests: day.Guests,
							Nights: day.Nights,
							Source: day.Source,
							Currency: day.Currency,
							OwnerRevenue: day.Revenue,
							Status: 'accepted'
						};
					}
				});

				const allReservationDetails = Object.values(reservationById);

				setProgress(90);
				setFadeOut(true);

				// 🔹 用 allReservationDetails（最新完整資料）生成最終 events
				const cachedEvents =
					allReservationDetails.map((res) => {
						const cleanedSource = cleanSource(
							res.Source
						);
						const checkInDate = format(
							parseISO(res.CheckIn),
							'yyyy-MM-dd'
						);
						const nights = res.Nights || 1;
						const endDate = format(
							addDays(
								parseISO(res.CheckIn),
								nights
							),
							'yyyy-MM-dd'
						);
						const style =
							channelStyles[
							cleanedSource.toLowerCase()
							] || channelStyles.default;
						return {
							id: `res-${res.Id}`,
							title: cleanedSource,
							start: checkInDate,
							end: endDate,
							backgroundColor:
								style.backgroundColor,
							extendedProps: {
								...res,
								Source: cleanedSource,
								Currency:
									res.Currency || 'AUD',
							},
						};
					});

				// 🆕 建立 reservationMap（用於 PopUp 判斷 booked 狀態）
				const newReservationMap = {};
				newReservationMap[listingId] = allReservationDetails.filter(res =>
					res.Status === 'accepted' || res.Status === 'Accepted'
				);
				setReservationMap(newReservationMap);
				console.log("🗺️ ReservationMap 建立完成:", newReservationMap);

				// ✅ 存入快取（保證事件完整）
				listingDataCache.set(listingId, {
					property: listing,
					calendarData,
					reportingData: reportingArray,
					events: cachedEvents, // ✅ 用最新的事件資料
					reservationMap: newReservationMap, // 🆕 加入這行
					currentRevenue,
					currentOccupancy,
					currentReservations,
					averageRevenuePerNight,
					totalNights,
					currentCurrency,
				});
				setEvents(cachedEvents);
				setProgress(100);
				setTimeout(
					() => setShowLoading(false),
					600
				);

				// ✅ 第二段：背景補齊 ±3 個月的 calendar 資料
				setTimeout(async () => {
					try {
						const fullStart = format(
							new Date(now.getFullYear(), now.getMonth() - 3, 1),
							'yyyy-MM-dd'
						);
						const fullEnd = format(
							new Date(now.getFullYear(), now.getMonth() + 4, 0),
							'yyyy-MM-dd'
						);

						const fullResult = await getCalendarBatchData({
							listingIds: [Number(listingId)],
							startDate: fullStart,
							endDate: fullEnd,
							perPage: 217,
							page: 1
						});

						const fullRawDays = fullResult?.success?.[0]?.calendar || [];
						const fullCalendarData = fullRawDays.map(day => ({
							Date: day.Date ? day.Date.split('T')[0] : null,
							Status: day.Status,
							Price: day.Price,
							Currency: day.Currency,
							Note: day.Note,
							ListingId: day.ListingId || Number(listingId),
							ReservationId: day.ReservationId,
							CheckIn: day.CheckIn,
							CheckOut: day.CheckOut,
							Nights: day.Nights,
							Source: day.Source,
							Revenue: day.Revenue,
						}));

						// 補齊 reservation events
						const fullReservationById = {};
						fullCalendarData.forEach(day => {
							if (day.ReservationId && day.CheckIn && day.CheckOut) {
								fullReservationById[day.ReservationId] = {
									Id: day.ReservationId,
									ListingId: day.ListingId || Number(listingId),
									CheckIn: day.CheckIn,
									CheckOut: day.CheckOut,
									Nights: day.Nights,
									Source: day.Source,
									Currency: day.Currency,
									OwnerRevenue: day.Revenue,
									Status: 'accepted'
								};
							}
						});

						const fullReservations = Object.values(fullReservationById);
						const fullEvents = fullReservations.map(res => {
							const cleanedSource = cleanSource(res.Source);
							const checkInDate = format(parseISO(res.CheckIn), 'yyyy-MM-dd');
							const nights = res.Nights || 1;
							const endDate = format(addDays(parseISO(res.CheckIn), nights), 'yyyy-MM-dd');
							const style = channelStyles[cleanedSource.toLowerCase()] || channelStyles.default;
							return {
								id: `res-${res.Id}`,
								title: cleanedSource,
								start: checkInDate,
								end: endDate,
								backgroundColor: style.backgroundColor,
								extendedProps: {
									...res,
									Source: cleanedSource,
									Currency: res.Currency || 'AUD',
								},
							};
						});

						// 更新 state 和快取
						setRawCalendarData(fullCalendarData);
						setEvents(fullEvents);
						listingDataCache.set(listingId, {
							...listingDataCache.get(listingId),
							calendarData: fullCalendarData,
							events: fullEvents,
						});
						console.log('✅ 背景補齊 ±3 個月 calendar 完成');
					} catch (err) {
						console.warn('Background calendar fetch failed:', err);
					}
				}, 100); // loading 結束後馬上開始跑，使用者感覺不到
			} catch (error) {
				console.error('fetchAllData error:', error);
				const status = error?.response?.status;
				const message =
					error?.response?.data?.message ||
					error.message;

				if (status === 404) {
					setHasNoListings(true);
				} else if (status === 500) {
					setErrorMessage(
						'Something went wrong. Please try again later.'
					);
				}
				setShowLoading(false);
			}
		};
		fetchAllData();
	}, [listingId]);

	useEffect(() => {
		if (!reportingData || !reportingMonth) return;

		const monthStart = new Date(
			reportingMonth.getFullYear(),
			reportingMonth.getMonth(),
			1
		);
		const monthEnd = new Date(
			reportingMonth.getFullYear(),
			reportingMonth.getMonth() + 1,
			0
		);

		const data = reportingData.find((r) => {
			const startDate = format(
				new Date(r?.StartDate),
				'yyyy-MM-dd'
			);
			const expectedStart = format(
				monthStart,
				'yyyy-MM-dd'
			);
			return startDate === expectedStart;
		});

		if (data) {
			setCurrentRevenue(data.TotalOwnerRevenue || 0);
			setCurrentOccupancy(data.OccupancyRate || 0);
			setCurrentReservations(
				data.TotalReservations || 0
			);
			setAverageRevenuePerNight(
				data.AverageRevenuePerNight || 0
			);
			setTotalNights(data.TotalNights || 0);
			setCurrentCurrency(data.Currency || 'AUD');
		} else {
			// 加入 fallback
			setCurrentRevenue(0);
			setCurrentOccupancy(0);
			setCurrentReservations(0);
			setAverageRevenuePerNight(0);
			setTotalNights(0);
		}
	}, [reportingData, reportingMonth]);

	const updateCalendarPricesAPI = async (listingId, startDate, endDate, newPrice, weekdays, weekdayPrices, bookedDates = []) => {
		try {
			// 檢查權限
			if (!permissionChecks.canEditPrices) {
				throw new Error("You do not have permission to edit prices.");
			}

			const start = new Date(startDate);
			const end = new Date(endDate);
			const calendar = [];
			const bookedDatesSet = new Set(bookedDates || []);
			console.log("已預訂的日期:", Array.from(bookedDatesSet));

			const isNewFormat = weekdayPrices && Array.isArray(weekdayPrices);


			const datesToUpdate = new Set();
			if (isNewFormat) {
				// 新格式：按星期幾設定不同價格
				let priceMap = {};
				weekdayPrices.forEach(({ weekday, newPrice }) => {
					priceMap[weekday] = Number(newPrice);
				});

				console.log("價格對應表:", priceMap);

				const current = new Date(start);
				const addedDates = new Set(); // 🆕 用來防止重複添加相同日期

				while (current <= end) {
					const weekday = current.getDay();
					const dateStr = current.toISOString().split('T')[0];

					console.log(`檢查日期: ${dateStr}, 星期${weekday}, 是否有價格: ${priceMap[weekday] !== undefined}, 是否 booked: ${bookedDatesSet.has(dateStr)}`);

					if (priceMap[weekday] !== undefined && !addedDates.has(dateStr) && !bookedDatesSet.has(dateStr)) {
						calendar.push({
							startDate: dateStr,
							endDate: dateStr,
							price: priceMap[weekday]
						});
						addedDates.add(dateStr);
						datesToUpdate.add(dateStr);
						console.log(`添加日期: ${dateStr}, 價格: ${priceMap[weekday]}`);
					} else if (bookedDatesSet.has(dateStr)) {
						console.log(`跳過已預訂日期: ${dateStr}`);
					}

					current.setDate(current.getDate() + 1);
				}

				console.log("最終 calendar 陣列:", calendar);

			} else {
				// 舊格式：指定星期幾使用同一價格
				if (weekdays && weekdays.length > 0) {
					const current = new Date(start);
					while (current <= end) {
						const weekday = current.getDay();
						const dateStr = current.toISOString().split('T')[0];

						if (weekdays.includes(weekday) && !bookedDatesSet.has(dateStr)) {
							calendar.push({
								startDate: dateStr,
								endDate: dateStr,
								price: Number(newPrice)
							});
							datesToUpdate.add(dateStr);
						} else if (bookedDatesSet.has(dateStr)) {
							console.log(`⚠️ 跳過已預訂日期: ${dateStr}`);
						}
						current.setDate(current.getDate() + 1);
					}
				} else {
					// 如果沒有指定星期幾，則更新整個日期範圍
					const current = new Date(start);
					while (current <= end) {
						const dateStr = current.toISOString().split('T')[0];

						if (!bookedDatesSet.has(dateStr)) {
							calendar.push({
								startDate: dateStr,
								endDate: dateStr,
								price: Number(newPrice)
							});
							datesToUpdate.add(dateStr);
						} else {
							console.log(`⚠️ 跳過已預訂日期: ${dateStr}`);
						}
						current.setDate(current.getDate() + 1);
					}
				}
			}
			// 如果沒有指定星期幾，則更新整個日期範圍

			if (calendar.length === 0) {
				throw new Error("No dates available to update.");
			}

			// 準備 API 請求格式
			const priceUpdates = [{
				listingId: Number(listingId),
				calendar: calendar
			}];

			console.log("🚀 發送價格更新請求:");
			console.log("📊 更新數量:", calendar.length, "個日期");
			console.log("📝 請求資料:", JSON.stringify(priceUpdates, null, 2));

			// 🔥 標記這些日期為「更新中」
			setUpdatingDates(datesToUpdate);

			// 呼叫 API
			console.log("正在呼叫 API...");
			const result = await updateCalendarPricesBulk(priceUpdates);

			console.log("API 更新成功:", result);

			//  重要：等待 1 秒讓後端資料完全同步
			console.log("等待後端資料同步...");
			await new Promise(resolve => setTimeout(resolve, 1000));

			// API 成功後重新獲取日曆資料 — 帶上剛剛實際被改到的日期範圍，
			// 確保就算編輯的是目前檢視月份 +2 / +3 這種超出預設窗口的月份，
			// 存檔後也會被重新抓回來顯示。
			console.log("開始重新載入日曆資料...");
			await refreshCalendarData({ startDate, endDate });

			// Clear updating state
			setUpdatingDates(new Set());

			return { success: true, updatedCount: calendar.length, result };
		} catch (error) {
			console.error('Price update failed:', error);

			// 清除更新標記
			setUpdatingDates(new Set());

			throw error;
		}
	};

	// 重新載入日曆資料
	// 存/改價格後呼叫：extraRange 是這次實際被編輯的日期範圍（{startDate, endDate}）。
	// 沒帶的話沿用舊行為，只抓「目前檢視月份 ±1」；帶了就聯集進去，
	// 避免編輯的是目前檢視月份以外（例如切到 +2 / +3 月才編輯）時抓不回來。
	const refreshCalendarData = async (extraRange = null) => {
		try {
			setIsRefreshing(true);
			console.log("正在重新獲取日曆資料...");

			// 計算當前顯示月份的範圍（前後各加一個月）
			let viewStart = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
			let viewEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 2, 0);

			if (extraRange?.startDate && extraRange?.endDate) {
				const extraStartRaw = new Date(extraRange.startDate);
				const extraEndRaw = new Date(extraRange.endDate);
				const extraStart = new Date(extraStartRaw.getFullYear(), extraStartRaw.getMonth(), 1);
				const extraEnd = new Date(extraEndRaw.getFullYear(), extraEndRaw.getMonth() + 1, 0);
				if (extraStart < viewStart) viewStart = extraStart;
				if (extraEnd > viewEnd) viewEnd = extraEnd;
			}

			const fetchStartStr = format(viewStart, 'yyyy-MM-dd');
			const fetchEndStr = format(viewEnd, 'yyyy-MM-dd');

			// 用 batch 重新獲取日曆資料
			const batchResult = await getCalendarBatchData({
				listingIds: [Number(listingId)],
				startDate: fetchStartStr,
				endDate: fetchEndStr,
				perPage: 217,
				page: 1
			});

			const rawDays = batchResult?.success?.[0]?.calendar || [];
			const freshDays = rawDays.map(day => ({
				Date: day.Date ? day.Date.split('T')[0] : null,
				Status: day.Status,
				Price: day.Price,
				Currency: day.Currency,
				Note: day.Note,
				ListingId: day.ListingId || Number(listingId),
				ReservationId: day.ReservationId,
				CheckIn: day.CheckIn,
				CheckOut: day.CheckOut,
				Nights: day.Nights,
				Source: day.Source,
				Revenue: day.Revenue,
			}));

			console.log("獲取到新的日曆資料:", freshDays.length, "天");

			// 合併：這次抓取範圍以外、先前透過切換月份載入過的日期要保留；
			// 範圍以內的一律用剛抓到的新資料整批換掉（確保剛改的價格生效）。
			const keptDays = rawCalendarDataRef.current.filter(d => {
				const dateStr = d.Date ? d.Date.split('T')[0] : '';
				return !dateStr || dateStr < fetchStartStr || dateStr > fetchEndStr;
			});
			const newCalendarData = [...keptDays, ...freshDays];

			setRawCalendarData(newCalendarData);

			// ✅ 從「合併後的完整資料」建 reservation，而不是只用這次抓到的資料，
			// 否則抓取範圍以外月份的 reservation/events 會被一併清空。
			const reservationById = {};
			newCalendarData.forEach(day => {
				if (day.ReservationId && day.CheckIn && day.CheckOut) {
					reservationById[day.ReservationId] = {
						Id: day.ReservationId,
						ListingId: day.ListingId || Number(listingId),
						CheckIn: day.CheckIn,
						CheckOut: day.CheckOut,
						// Guests: day.Guests,
						Nights: day.Nights,
						Source: day.Source,
						Currency: day.Currency,
						OwnerRevenue: day.Revenue,
						Status: 'accepted'
					};
				}
			});

			const allReservations = Object.values(reservationById);

			const newEvents = allReservations.map(res => {
				const cleanedSource = cleanSource(res.Source);
				const checkInDate = format(parseISO(res.CheckIn), 'yyyy-MM-dd');
				const nights = res.Nights || 1;
				const endDate = format(addDays(parseISO(res.CheckIn), nights), 'yyyy-MM-dd');
				const style = channelStyles[cleanedSource.toLowerCase()] || channelStyles.default;

				return {
					id: `res-${res.Id}`,
					title: cleanedSource,
					start: checkInDate,
					end: endDate,
					backgroundColor: style.backgroundColor,
					extendedProps: {
						...res,
						Source: cleanedSource,
						Currency: res.Currency || 'AUD',
					},
				};
			});
			// 更新 reservationMap
			const newReservationMap = {};
			newReservationMap[listingId] = allReservations.filter(res =>
				res.Status === 'accepted' || res.Status === 'Accepted'
			);
			setReservationMap(newReservationMap);
			console.log("🗺️ ReservationMap 更新完成:", newReservationMap[listingId]?.length || 0, "筆");

			// 更新 events
			setEvents(newEvents);

			console.log("✅ 日曆資料更新完成，events 數量:", newEvents.length);

			// 更新快取
			if (listingDataCache.has(listingId)) {
				const cached = listingDataCache.get(listingId);
				listingDataCache.set(listingId, {
					...cached,
					calendarData: newCalendarData,
					events: newEvents,
					reservationMap: newReservationMap,
				});
				console.log("💾 快取已更新");
			}

		} catch (error) {
			console.error("❌ 重新獲取日曆資料失敗:", error);
			throw error; // 讓呼叫者知道失敗了
		} finally {
			setIsRefreshing(false);
		}
	};



	useEffect(() => {
		console.log('📊 reportingData =', reportingData);
	}, [reportingData]);

	if (!permissionChecks.canViewCalendar) {
		return (
			<Container>
				<div style={{ padding: '20px', textAlign: 'center' }}>
					<h2>⚠️ 無權限存取</h2>
					<p>您沒有權限查看此房源的日曆資料</p>
				</div>
			</Container>
		);
	}

	console.log('📅 CalendarSingle 權限狀態:', permissionChecks);

	if (!showLoading && errorMessage) {
		return (
			<ErrorWarp>
				<ErrorImg src="../Assets/mrK05.png" />
				<ErrorP>{errorMessage}</ErrorP>
			</ErrorWarp>
		);
	}

	if (!showLoading && hasNoListings) {
		return (
			<ErrorWarp>
				<ErrorImg src="../Assets/mrK05.png" />
				<ErrorP>
					Sorry, You Don't have any Property
					Listing
				</ErrorP>
			</ErrorWarp>
		);
	}

	if (showLoading) {
		return (
			<LoadingWrapper
				style={{
					opacity: fadeOut ? 0 : 1,
					transition: 'opacity 0.6s ease',
				}}>
				<p
					style={{
						fontSize: '20px',
						color: '#61CBC9',
						fontWeight: 'bold',
					}}>
					KozyGuring...{' '}
					{Math.min(displayProgress, 100)}%
				</p>
				<model-viewer
					src={runningModel}
					autoplay
					animation-name="Run"
					camera-controls
					style={{
						width: '150px',
						height: '150px',
					}}
				/>
			</LoadingWrapper>
		);
	}

	const updateCalendarButtonStates = (
		calendarApi,
		minDate,
		maxDate
	) => {
		if (!calendarApi) return;

		const currentDate = calendarApi.getDate();
		const prevButton = document.querySelector(
			'.fc-prevCustom-button'
		);
		const nextButton = document.querySelector(
			'.fc-nextCustom-button'
		);

		const prevMonth = new Date(
			currentDate.getFullYear(),
			currentDate.getMonth() - 1,
			1
		);
		const nextMonth = new Date(
			currentDate.getFullYear(),
			currentDate.getMonth() + 1,
			1
		);

		if (prevButton) {
			if (minDate && prevMonth < new Date(minDate)) {
				prevButton.classList.add(
					'fc-button-disabled'
				);
			} else {
				prevButton.classList.remove(
					'fc-button-disabled'
				);
			}
		}

		if (nextButton) {
			if (maxDate && nextMonth > new Date(maxDate)) {
				nextButton.classList.add(
					'fc-button-disabled'
				);
			} else {
				nextButton.classList.remove(
					'fc-button-disabled'
				);
			}
		}
	};

	return (
		<>
			<GlobalStyles />
			<Container style={{ position: 'relative' }}>
				{isLoadingMonth && (
					<div style={{
						position: 'fixed',
						top: 0, left: 0, right: 0, bottom: 0,
						backgroundColor: 'rgba(255,255,255,0.6)',
						zIndex: 99999,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
					}}>
						<p style={{ fontSize: '18px', color: '#61CBC9', fontWeight: 'bold' }}>
							Loading...
						</p>
					</div>
				)}
				{errorMessage && (
					<div
						style={{
							color: 'red',
							marginBottom: '16px',
						}}>
						⚠️ {errorMessage}
					</div>
				)}
				<LeftContainer>
					{userListings.length > 1 && (
						<Dropdown
							options={userListings
								.filter(l => l.ParentListingId === null && l.IsServicePms !== false)
								.map((l) => ({
									label: l.KozyGuruName,
									value: l.Id,
								}))
							}
							value={selectedListingId}
							onChange={(newListingId) => {
								const option = userListings.find(l => l.Id === newListingId);
								if (option?.IsServicePms === false) return;

								setSelectedListingId(newListingId);
								navigate(`/dashboard/calendar/${newListingId}`);
							}}
							defaultLabel="Select Property"
							isOpen={dropdownOpen}
							onOpen={() =>
								setDropdownOpen(true)
							}
							onClose={() =>
								setDropdownOpen(false)
							}
						/>
					)}
					{property ? (
						<ListingWrap>
							<ListingHeader>
								<div>
									<Subtitle>
										{
											property.KozyGuruName
										}
									</Subtitle>
									<Title>
										{property.Name}
									</Title>
								</div>
								<img
									src={
										property.Thumbnail ||
										'/fallback.jpg'
									}
									alt={property.Name}
								/>
							</ListingHeader>

							{currentRevenue !== null && (
								<InfoCardGroup>
									<InfoCard>
										<h1 style={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
											{formatCurrencySymbol(
												currentCurrency
											)}
											{Number(
												currentRevenue
											).toLocaleString(
												undefined,
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												}
											)}
										</h1>
										<p>Revenue</p>
									</InfoCard>
									<InfoCard>
										<h1>
											{
												currentOccupancy
											}
											%
										</h1>
										<p>
											Occupancy Rate
										</p>
									</InfoCard>
									{/* <InfoCard>
                                        <h1>
                                            AU${Number(averageRevenuePerNight).toLocaleString('en-AU', {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2
                                            })}
                                        </h1>
                                        <p>Avg / Night</p>
                                    </InfoCard> */}

									<InfoCard
										style={{
											flex: '1 1 100%',
										}}>
										<h1>
											{totalNights}{' '}
											<span
												style={{
													fontSize:
														'12px',
													color: '#888',
												}}>
												/ nights
											</span>
										</h1>
										<p>Total Nights</p>
									</InfoCard>
								</InfoCardGroup>
							)}
						</ListingWrap>
					) : (
						<p>KozyGuring...</p>
					)}
				</LeftContainer>

				<RightContainer>
					<FullCalendar
						key={key}
						fixedWeekCount={false}
						ref={calendarRef}
						initialDate={currentDate}
						plugins={[
							dayGridPlugin,
							interactionPlugin,
						]}
						initialView="dayGridMonth"
						displayEventTime={false}
						customButtons={{
							prevCustom: {
								text: '<',
								click: () => {
									const calendarApi =
										calendarRef.current?.getApi();
									if (
										!reportingMonth ||
										!minDate
									)
										return;

									const prevMonth =
										new Date(
											reportingMonth.getFullYear(),
											reportingMonth.getMonth() -
											1,
											1
										);
									const minAllowed =
										new Date(minDate);

									if (
										prevMonth >=
										minAllowed
									) {
										calendarApi.prev();
										updateCalendarButtonStates(
											calendarApi,
											minDate,
											maxDate
										); // ✅ 每次點擊都更新按鈕狀態
									}
								},
							},
							nextCustom: {
								text: '>',
								click: () => {
									const calendarApi =
										calendarRef.current?.getApi();
									if (
										!reportingMonth ||
										!maxDate
									)
										return;

									const nextMonth =
										new Date(
											reportingMonth.getFullYear(),
											reportingMonth.getMonth() +
											1,
											1
										);
									const maxAllowed =
										new Date(maxDate);

									if (
										nextMonth <=
										maxAllowed
									) {
										calendarApi.next();
										updateCalendarButtonStates(
											calendarApi,
											minDate,
											maxDate
										); // ✅ 每次點擊都更新按鈕狀態
									}
								},
							},
							refreshCustom: {
								hint: 'Refresh Calendar',
								click: async () => {
									if (isRefreshing) return;
									setIsRefreshing(true);
									try {
										await refreshCalendarData();
										setKey(prevKey => prevKey + 1);
										setToastMessage("Calendar refreshed successfully!");
										setShowToast(true);
										setTimeout(() => setShowToast(false), 3000);
									} catch (error) {
										console.error("Refresh failed:", error);
										setToastMessage("Failed to refresh calendar");
										setShowToast(true);
										setTimeout(() => setShowToast(false), 3000);
									} finally {
										setIsRefreshing(false);
									}
								},
							},
						}}

						headerToolbar={{
							start: 'prevCustom,nextCustom today refreshCustom',
							center: '',
							end: 'title',
						}}

						height="100%"
						contentHeight="100%"
						expandRows={true}
						events={events}
						buttonText={{
							today: 'Today',
						}}
						// 訂單（reservation）不可開啟改價 popup —— 已預訂日期不能改價。
						// 手機點擊訂單條會由 eventDidMount 的 touchstart 顯示詳情 tooltip；
						// 空白日期的改價入口走 dateClick（該處已有 booked / blocked 檢查）。
						eventClick={undefined}

						eventDidMount={(info) => {
							const {
								Source,
								OwnerRevenue,
								Nights,
								// Guests,
								CheckIn,
								CheckOut,
							} = info.event.extendedProps;

							const cleaned =
								cleanSource(Source);
							const style =
								channelStyles[
								cleaned.toLowerCase()
								] || channelStyles.default;
							const icon = style.icon;

							// 建立 wrapper
							const wrapper =
								document.createElement(
									'div'
								);
							wrapper.style.display = 'flex';
							wrapper.style.alignItems =
								'center';
							wrapper.style.justifyContent =
								'center';
							wrapper.style.color = '#fff';
							wrapper.style.fontSize = '12px';
							wrapper.style.gap = '6px';
							wrapper.style.maxWidth = '100%';
							wrapper.style.overflow =
								'hidden';
							wrapper.style.textOverflow =
								'ellipsis';
							wrapper.style.whiteSpace =
								'nowrap';
							wrapper.style.margin = '0 auto';

							const img =
								document.createElement(
									'img'
								);
							img.src = icon;
							img.style.width = '16px';
							img.style.height = '16px';

							const text =
								document.createElement(
									'span'
								);
							text.innerText = cleaned;

							wrapper.appendChild(img);
							wrapper.appendChild(text);

							// 清除原始內容並加上 wrapper
							info.el.innerHTML = '';
							info.el.appendChild(wrapper);

							// 樣式
							info.el.style.maxHeight =
								'30px';
							info.el.style.padding =
								'4px 6px';
							info.el.style.backgroundColor =
								style.backgroundColor;
							info.el.style.color = 'white';
							info.el.style.cursor =
								'pointer';
							info.el.style.border = 'none';
							info.el.style.boxShadow =
								style.borderColor
									? `inset 0px -4px 0px ${style.borderColor}`
									: 'none';

							// segment 判斷（跨多天事件）
							info.el.setAttribute(
								'data-event-id',
								info.event.id
							);
							const allSegments = Array.from(
								document.querySelectorAll(
									`.fc-event[data-event-id="${info.event.id}"]`
								)
							);
							allSegments.sort((a, b) => {
								const rectA =
									a.getBoundingClientRect();
								const rectB =
									b.getBoundingClientRect();
								return rectA.top ===
									rectB.top
									? rectA.left -
									rectB.left
									: rectA.top - rectB.top;
							});
							const index =
								allSegments.findIndex(
									(el) => el === info.el
								);

							// 圓角預設歸零
							info.el.style.borderRadius =
								'0px';
							info.el.style.borderTopLeftRadius =
								'0px';
							info.el.style.borderBottomLeftRadius =
								'0px';
							info.el.style.borderTopRightRadius =
								'0px';
							info.el.style.borderBottomRightRadius =
								'0px';

							if (allSegments.length === 1) {
								info.el.style.borderRadius =
									'0px';
							} else {
								if (index === 0) {
									info.el.style.borderTopLeftRadius =
										'0px';
									info.el.style.borderBottomLeftRadius =
										'0px';
								}
								if (
									index ===
									allSegments.length - 1
								) {
									info.el.style.borderTopRightRadius =
										'0px';
									info.el.style.borderBottomRightRadius =
										'0px';
								}
								if (index > 0) {
									const prev =
										allSegments[
										index - 1
										];
									prev.style.borderTopRightRadius =
										'0px';
									prev.style.borderBottomRightRadius =
										'0px';
								}
								if (
									index <
									allSegments.length - 1
								) {
									info.el.style.borderTopLeftRadius =
										'0px';
									info.el.style.borderBottomLeftRadius =
										'0px';
								}
							}

							// 🔄 CheckOut 與下一筆 CheckIn 同天 → 寬度切半處理
							const visualCheckOut = format(
								addDays(
									parseISO(CheckOut),
									1
								),
								'yyyy-MM-dd'
							);
							const checkInDate = format(
								parseISO(CheckIn),
								'yyyy-MM-dd'
							);

							const overlappingEvents =
								events.filter((e) => {
									if (
										e.id ===
										info.event.id
									)
										return false;
									const otherIn =
										e.extendedProps
											?.CheckIn;
									const otherOut =
										e.extendedProps
											?.CheckOut;
									if (
										!otherIn ||
										!otherOut
									)
										return false;

									const otherInDate =
										format(
											parseISO(
												otherIn
											),
											'yyyy-MM-dd'
										);
									const otherOutVisual =
										format(
											addDays(
												parseISO(
													otherOut
												),
												1
											),
											'yyyy-MM-dd'
										);

									return (
										visualCheckOut ===
										otherInDate ||
										checkInDate ===
										otherOutVisual
									);
								});

							const isFirstSegment =
								index === 0;
							const isLastSegment =
								index ===
								allSegments.length - 1;

							// 若為首段或尾段，且與其他事件 overlap 在同一天 → 切半顯示
							if (
								(isFirstSegment ||
									isLastSegment) &&
								overlappingEvents.length > 0
							) {
								const other =
									overlappingEvents[0];
								const otherCheckIn = format(
									parseISO(
										other.extendedProps
											.CheckIn
									),
									'yyyy-MM-dd'
								);
								const otherCheckOutVisual =
									format(
										addDays(
											parseISO(
												other
													.extendedProps
													.CheckOut
											),
											1
										),
										'yyyy-MM-dd'
									);

								const sameDayOverlap =
									(visualCheckOut ===
										otherCheckIn &&
										isLastSegment) ||
									(checkInDate ===
										otherCheckOutVisual &&
										isFirstSegment);

								if (sameDayOverlap) {
									if (isLastSegment) {
										info.el.style.width =
											'50%';
										info.el.style.marginLeft =
											'0%';
										info.el.style.marginTop =
											'2px';
									} else if (
										isFirstSegment
									) {
										info.el.style.width =
											'50%';
										info.el.style.marginLeft =
											'100%';
									}
								}
							}

							info.el.style.width = '100%';
							info.el.style.marginLeft = '0';
							const currencySymbol =
								formatCurrencySymbol(
									info.event.extendedProps
										.Currency || 'AUD'
								);

							const tooltipHtml = `
    <div style="text-align:left;">
      <strong>${escapeHtml(cleaned)}</strong><br/>
      Revenue: ${escapeHtml(currencySymbol)}${Number(
								OwnerRevenue
							).toLocaleString()}<br/>
      Nights: ${escapeHtml(Nights ?? '-')}<br/>
      Check-In: ${CheckIn
									? format(parseISO(CheckIn), 'yyyy-MM-dd')
									: '-'
								}<br/>
      Check-Out: ${CheckOut
									? format(parseISO(CheckOut), 'yyyy-MM-dd')
									: '-'
								}
    </div>
`;

							wrapper.addEventListener(
								'mouseenter',
								(e) => {
									showTooltip(
										tooltipHtml,
										e
									);
								}
							);
							wrapper.addEventListener(
								'mousemove',
								(e) => {
									showTooltip(
										tooltipHtml,
										e
									);
								}
							);

							wrapper.addEventListener(
								'touchstart',
								(e) => {
									showTooltip(
										`
        <div style="text-align:left;">
          <strong>${escapeHtml(cleaned)}</strong><br/>
          Revenue: ${escapeHtml(currencySymbol)}${Number(OwnerRevenue).toLocaleString()}<br/>
          Nights: ${escapeHtml(Nights ?? '-')}<br/>
          Check-In: ${CheckIn
											? format(
												parseISO(CheckIn),
												'yyyy-MM-dd'
											)
											: '-'
										}<br/>
          Check-Out: ${CheckOut
											? format(
												parseISO(CheckOut),
												'yyyy-MM-dd'
											)
											: '-'
										}
        </div>`,
										e.touches[0]
									);

									e.stopPropagation(); // 避免觸發 document 的 touchstart 導致立即關閉
								}
							);

							wrapper.addEventListener(
								'mouseleave',
								hideTooltip
							);
						}}

						dayCellDidMount={(arg) => {
							const today = new Date();
							const cellDate = arg.date;
							const isPast = cellDate < new Date(today.getFullYear(), today.getMonth(), today.getDate());
							if (!isPast) return;

							const dateString = format(cellDate, 'yyyy-MM-dd');
							const rawDataForThisDate = rawCalendarData.find((day) => {
								const eventDateString = format(new Date(day.Date || day.date), 'yyyy-MM-dd');
								return eventDateString === dateString;
							});
							const status = rawDataForThisDate?.Status?.toLowerCase();
							const isBooked = status === 'booked';

							// 整個 cell 的 frame
							const frameEl = arg.el.querySelector('.fc-daygrid-day-frame');
							if (!frameEl) return;

							frameEl.style.backgroundColor = '#f6f6f6';

							if (!isBooked) {
								// 插入一個真正鋪滿底層的斜線 div
								const stripeDiv = document.createElement('div');
								stripeDiv.style.cssText = `
            position: absolute;
            inset: 0;
            background-image: repeating-linear-gradient(135deg, rgba(0,0,0,0.05) 0, rgba(0,0,0,0.05) 2px, transparent 2px, transparent 6px);
            pointer-events: none;
            z-index: 0;
        `;
								frameEl.style.position = 'relative';
								frameEl.insertBefore(stripeDiv, frameEl.firstChild);
							}
						}}


						dateClick={permissionChecks.canEditPrices ? (info) => {
							// 檢查是否為過去日期
							const clickedDate = new Date(info.dateStr);
							const today = new Date();
							today.setHours(0, 0, 0, 0);

							if (clickedDate < today) {
								return; // 過去日期不可編輯
							}

							// 🔥 找到當天的日曆資料
							const dateString = info.dateStr;
							const rawDataForThisDate = rawCalendarData.find((day) => {
								const eventDateString = format(
									new Date(day.Date || day.date),
									'yyyy-MM-dd'
								);
								return eventDateString === dateString;
							});

							const price = rawDataForThisDate?.Price;
							const currency = rawDataForThisDate?.Currency || 'AUD';
							const status = rawDataForThisDate?.Status?.toLowerCase();
							const isBooked = status === 'booked';

							// 🔥 如果已經被預訂，顯示 Toast 並返回
							const isBlocked =
								status === 'blocked' ||
								status === 'block' ||
								status === 'unavailable';

							if (isBooked || isBlocked) {
								setToastMessage(
									isBooked
										? "This date is already booked."
										: "This date is blocked and cannot be edited."
								);
								setShowToast(true);
								setTimeout(() => setShowToast(false), 3000);
								return;
							}


							// 🔥 將 rawCalendarData (陣列) 轉換為 PopUp 期望的格式 (按月份分組的物件)
							const calendarDataForPopup = {};
							rawCalendarData.forEach((day) => {
								const dayDate = new Date(day.Date || day.date);
								const year = dayDate.getFullYear();
								const month = String(dayDate.getMonth() + 1).padStart(2, '0');
								const key = `${listingId}-${year}-${month}`;

								if (!calendarDataForPopup[key]) {
									calendarDataForPopup[key] = [];
								}

								calendarDataForPopup[key].push({
									id: day.Id,
									date: day.Date || day.date,
									price: Number(day.Price) || 0,
									currency: day.Currency || 'AUD',
									status: day.Status?.toLowerCase() || 'available',
									note: day.Note
								});
							});

							console.log("🔍 CalendarSingle 傳給 PopUp 的資料:");
							console.log("📊 calendarDataForPopup keys:", Object.keys(calendarDataForPopup));
							console.log("📊 第一個月份範例:", calendarDataForPopup[Object.keys(calendarDataForPopup)[0]]?.slice(0, 2));
							console.log("📊 當前選擇日期 price:", price, "currency:", currency);
							console.log("📊 reservationMap:", reservationMap);

							// 🔥 打開 Popup（價格編輯模式）
							setPopupData({
								mode: "price",
								listingId: listingId,
								startDate: dateString,
								endDate: dateString,
								price: price,
								currency: currency,
								listing: {
									listingId: listingId,
									name: property?.Name,
									kozyName: property?.KozyGuruName,
									bedrooms: property?.Bedrooms,
									bathrooms: property?.Bathrooms,
									// guests: property?.Guests,
									image: property?.Thumbnail,
								},
								reservationMap: reservationMap, // 🔥 傳入真實的 reservationMap
								calendarData: calendarDataForPopup, // ✅ 使用轉換後的格式
								hideBlockTab: true
							});
						} : undefined}



						dayCellContent={(arg) => {
							const dateString = format(
								arg.date,
								'yyyy-MM-dd'
							);
							const rawDataForThisDate =
								rawCalendarData.find(
									(day) => {
										const eventDateString =
											format(
												new Date(
													day.Date ||
													day.date
												),
												'yyyy-MM-dd'
											);
										return (
											eventDateString ===
											dateString
										);
									}
								);

							const price =
								rawDataForThisDate?.Price;
							const currency =
								rawDataForThisDate?.Currency ||
								'AUD';
							const status =
								rawDataForThisDate?.Status?.toLowerCase();
							const isBooked =
								status === 'booked';
							const isBlocked =
								status === 'blocked' ||
								status === 'block' ||
								status === 'unavailable';



							// ✅ 過去日期判斷
							const today = new Date();
							const cellDate = new Date(
								arg.date
							);
							const isPast =
								cellDate <
								new Date(
									today.getFullYear(),
									today.getMonth(),
									today.getDate()
								);

							return (
								<div
									style={{
										minHeight: '60px',
										width: '100%',
										height: '100%',
										inset: 0,
										display: 'flex',
										flexDirection: 'column',
										justifyContent: 'flex-start',
										alignItems: 'flex-start',
										position: 'relative',
										boxSizing: 'border-box',
										ackgroundColor: '#fff',
										opacity: 1,
										zIndex: 0,
										paddingTop: '24px',
									}}>
									{/* 日期文字靠左上 */}
									<DaySpan
										style={{
											margin: '2px 0 0 4px',
											fontWeight: 500,
										}}>
										{arg.dayNumberText}
									</DaySpan>






									{/* ✅ 價格顯示（含過去日期、未預訂） */}
									{permissionChecks.canViewPrices &&
										!isBooked &&
										!isBlocked &&
										price && (
											<PriceTag>
												{formatCurrencySymbol(currency)}
												{price}
											</PriceTag>
										)}
								</div>
							);
						}}
						datesSet={(arg) => {
							const expandedStart = format(
								startOfWeek(arg.start, {
									weekStartsOn: 0,
								}),
								'yyyy-MM-dd'
							);
							const expandedEnd = format(
								endOfWeek(arg.end, {
									weekStartsOn: 0,
								}),
								'yyyy-MM-dd'
							);
							const viewMid = new Date(
								(arg.start.getTime() +
									arg.end.getTime()) /
								2
							);

							setReportingMonth(viewMid);
							setDateRange({
								start: expandedStart,
								end: expandedEnd,
							});
							setReservationDateRange({
								start: expandedStart,
								end: expandedEnd,
							});
							// 記錄視圖中間日期（arg.start 可能落在上個月，refresh 重掛後會跳錯月份）
							setCurrentDate(viewMid);
							handleDatesSet(viewMid);

							console.log(
								`📅 日曆 UI 展開區間：${expandedStart} ~ ${expandedEnd}`
							);

							const calendarApi =
								calendarRef.current?.getApi();
							const toolbarEl =
								calendarApi?.el.querySelector(
									'.fc-toolbar-chunk'
								);

							if (toolbarEl) {
								const prevButton =
									toolbarEl.querySelector(
										'.fc-prevCustom-button'
									);
								const nextButton =
									toolbarEl.querySelector(
										'.fc-nextCustom-button'
									);

								if (
									!prevButton ||
									!nextButton
								)
									return;

								const prevMonth = new Date(
									viewMid.getFullYear(),
									viewMid.getMonth() - 1,
									1
								);
								const nextMonth = new Date(
									viewMid.getFullYear(),
									viewMid.getMonth() + 1,
									1
								);

								const now = new Date();
								const min = new Date(
									now.getFullYear(),
									now.getMonth() - 3,
									1
								);
								const max = new Date(
									now.getFullYear(),
									now.getMonth() + 4,
									0
								);

								// 🔽 控制 prev 按鈕
								if (prevMonth < min) {
									prevButton.classList.add(
										'fc-button-disabled'
									);
									prevButton.disabled = true;
								} else {
									prevButton.classList.remove(
										'fc-button-disabled'
									);
									prevButton.disabled = false;
								}

								// 🔼 控制 next 按鈕
								if (nextMonth >= max) {
									nextButton.classList.add(
										'fc-button-disabled'
									);
									nextButton.disabled = true;
								} else {
									nextButton.classList.remove(
										'fc-button-disabled'
									);
									nextButton.disabled = false;
								}
							}
						}}
					/>
				</RightContainer>
				{/* **彈出視窗** */}
				{popupData && (
					<PopupOverlay onClick={(e) => e.stopPropagation()}>
						<PopupContent onClick={(e) => e.stopPropagation()}>
							<PopUp
								data={popupData}
								onClose={() => {
									// 🆕 只有在不是更新中才允許關閉
									if (!isUpdatingPrice) {
										setPopupData(null);
									}
								}}
								isLoading={isUpdatingPrice}
								showToast={(message, isError = false) => {
									setToastMessage(message);
									setShowToast(true);
									setTimeout(() => setShowToast(false), 5000);
								}}
								onSave={async ({ mode, listingId, startDate, endDate, newPrice, weekdays, weekdayPrices, bookedDates }) => {
									// 處理 Price Edit
									if (mode === "price") {
										console.log("=== Price Update Started ===");

										// 🆕 防止重複請求
										if (isUpdatingPrice) {
											console.log("Processing, please do not click repeatedly");
											return;
										}

										if (!permissionChecks.canEditPrices) {
											alert("You don't have permission to update the price");
											return;
										}

										// 🆕 設定 loading 狀態
										console.log("Updating price, setting loading state");
										setIsUpdatingPrice(true);

										try {
											// 🆕 呼叫實際 API
											const result = await updateCalendarPricesAPI(
												listingId,
												startDate,
												endDate,
												newPrice,
												weekdays,
												weekdayPrices,
												bookedDates
											);

											if (result.success) {
												const start = new Date(startDate);
												const end = new Date(endDate);
												const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;

												console.log("實際日期範圍天數:", daysDiff);
												console.log("API 回報更新數量:", result.updatedCount);

												// 🔥 先關閉 popup
												setPopupData(null);

												// 🔥 顯示成功 Toast
												const msg = `Prices updated successfully: ${result.updatedCount} date${result.updatedCount > 1 ? 's' : ''} updated.\n\nPrices may take a few minutes to sync. Please refresh if not updated immediately.`;
												setToastMessage(msg);
												setShowToast(true);
												setTimeout(() => {
													setShowToast(false);
												}, 3000);
											} else {
												// 🔥 使用 Toast 而非 alert
												setToastMessage("Failed to update the price.");
												setShowToast(true);
												setTimeout(() => {
													setShowToast(false);
												}, 3000);
											}
										} catch (error) {
											console.error("Price update error:", error);

											// 🔥 使用 Toast 而非 alert
											const errMsg = `Price update failed: ${error.message}`;
											setToastMessage(errMsg);
											setShowToast(true);
											setTimeout(() => {
												setShowToast(false);
											}, 3000);
										} finally {
											// 🆕 無論成功失敗都要重置 loading 狀態
											setIsUpdatingPrice(false);
										}

										return;
									}
								}}
							/>
						</PopupContent>
					</PopupOverlay>
				)}
			</Container>

			{/* 🆕 Toast 通知 */}
			{showToast && (
				<ToastContainer isError={toastMessage.includes('failed') || toastMessage.includes('valid price')}>
					<div>{toastMessage}</div>
					<ToastCloseButton
						isError={toastMessage.includes('failed') || toastMessage.includes('valid price')}
						onClick={() => setShowToast(false)}
					>
						×
					</ToastCloseButton>
				</ToastContainer>
			)}

			{/* 🆕 右上角「Updating...」指示器 */}
			{isRefreshing && (
				<UpdateIndicator>
					<SpinnerIcon />
					Updating...
				</UpdateIndicator>
			)}
		</>
	);
};

export default CalendarSingle;